//#region Imports
// React
import { useMemo, useState, useEffect, createContext } from "react";

// MUI
import { ThemeProvider, CssBaseline } from "@mui/material";
import {
  teal,
  grey,
  green,
  lightGreen,
  orange,
  lightBlue,
  deepPurple,
  pink,
  indigo,
  red,
} from "@mui/material/colors";

// Theme Tools
import { themesCollection, defaultThemeName } from "./themes collection";
import CustomGlobalStyles from "./Custom Global Styles";
import createAppTheme from "./theme";

// Utils
import {
  setLocalStorageValue,
  getLocalStorageValue,
} from "../additional/functions";
//#endregion

export const ThemeSwitcherContext = createContext();

export const ThemeProviderWrapper = ({ children }) => {
  const [themeName, setThemeName] = useState(
    () => getLocalStorageValue("themeName") || defaultThemeName
  );

  useEffect(() => {
    setLocalStorageValue("themeName", themeName);
  }, [themeName]);

  const theme = useMemo(
    () =>
      createAppTheme(
        themesCollection[themeName] || themesCollection[defaultThemeName]
      ),
    [themeName]
  );

  //#region DRS Pipe Colors
  const readyChipColor = green,
    toVerifyChipColor = orange,
    inProcessChipColor = lightGreen,
    skippedChipColor = lightBlue,
    onHoldChipColor = grey,
    unknownChipColor = teal;

  //#region Color Defining
  // Light theme
  theme.colorSchemes.light.palette.drsPipeReadyChip =
    theme.palette.augmentColor({
      color: { main: readyChipColor[600] },
      name: "drsPipeReadyChip",
    });
  theme.colorSchemes.light.palette.drsPipeToVerifyChip =
    theme.palette.augmentColor({
      color: { main: toVerifyChipColor[600] },
      name: "drsPipeToVerifyChip",
    });
  theme.colorSchemes.light.palette.drsPipeInProcessChip =
    theme.palette.augmentColor({
      color: { main: inProcessChipColor[500] },
      name: "drsPipeInProcessChip",
    });
  theme.colorSchemes.light.palette.drsPipeSkippedChip =
    theme.palette.augmentColor({
      color: { main: skippedChipColor[500] },
      name: "drsPipeSkippedChip",
    });
  theme.colorSchemes.light.palette.drsPipeOnHoldChip =
    theme.palette.augmentColor({
      color: { main: onHoldChipColor[400] },
      name: "drsPipeOnHoldChip",
    });
  theme.colorSchemes.light.palette.drsPipeUnknownChip =
    theme.palette.augmentColor({
      color: { main: unknownChipColor[400] },
      name: "drsPipeUnknownChip",
    });

  // Dark theme
  theme.colorSchemes.dark.palette.drsPipeReadyChip = theme.palette.augmentColor(
    {
      color: { main: readyChipColor[800] },
      name: "drsPipeReadyChip",
    }
  );
  theme.colorSchemes.dark.palette.drsPipeToVerifyChip =
    theme.palette.augmentColor({
      color: { main: toVerifyChipColor[800] },
      name: "drsPipeToVerifyChip",
    });
  theme.colorSchemes.dark.palette.drsPipeInProcessChip =
    theme.palette.augmentColor({
      color: { main: inProcessChipColor[700] },
      name: "drsPipeInProcessChip",
    });
  theme.colorSchemes.dark.palette.drsPipeSkippedChip =
    theme.palette.augmentColor({
      color: { main: skippedChipColor[700] },
      name: "drsPipeSkippedChip",
    });
  theme.colorSchemes.dark.palette.drsPipeOnHoldChip =
    theme.palette.augmentColor({
      color: { main: onHoldChipColor[600] },
      name: "drsPipeOnHoldChip",
    });
  theme.colorSchemes.dark.palette.drsPipeUnknownChip =
    theme.palette.augmentColor({
      color: { main: unknownChipColor[600] },
      name: "drsPipeUnknownChip",
    });
  //#endregion
  //#endregion

  //#region Alias Keyword Constructor Colors
  const titleColor = deepPurple,
    jobLevelColor = pink,
    jobAreaColor = indigo,
    customLevelColor = green,
    mappingColor = red;
  //#region Color Defining
  // Light theme
  theme.colorSchemes.light.palette.ACTitle = theme.palette.augmentColor({
    color: { main: titleColor["A700"] },
    name: "title",
  });
  theme.colorSchemes.light.palette.ACJobLevel = theme.palette.augmentColor({
    color: { main: jobLevelColor["A700"] },
    name: "jobLevel",
  });
  theme.colorSchemes.light.palette.ACJobArea = theme.palette.augmentColor({
    color: { main: jobAreaColor["A700"] },
    name: "jobArea",
  });
  theme.colorSchemes.light.palette.ACCustomLevel = theme.palette.augmentColor({
    color: { main: customLevelColor["A700"] },
    name: "customLevel",
  });
  theme.colorSchemes.light.palette.ACMapping = theme.palette.augmentColor({
    color: { main: mappingColor["A700"] },
    name: "mapping",
  });
  // Dark theme
  theme.colorSchemes.dark.palette.ACTitle = theme.palette.augmentColor({
    color: { main: titleColor["A100"] },
    name: "title",
  });
  theme.colorSchemes.dark.palette.ACJobLevel = theme.palette.augmentColor({
    color: { main: jobLevelColor["A200"] },
    name: "jobLevel",
  });
  theme.colorSchemes.dark.palette.ACJobArea = theme.palette.augmentColor({
    color: { main: jobAreaColor["A200"] },
    name: "jobArea",
  });
  theme.colorSchemes.dark.palette.ACCustomLevel = theme.palette.augmentColor({
    color: { main: customLevelColor["A200"] },
    name: "customLevel",
  });
  theme.colorSchemes.dark.palette.ACMapping = theme.palette.augmentColor({
    color: { main: mappingColor["A200"] },
    name: "mapping",
  });
  //#endregion
  //#endregion

  return (
    <ThemeSwitcherContext.Provider value={{ themeName, setThemeName }}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <CustomGlobalStyles />
        {children}
      </ThemeProvider>
    </ThemeSwitcherContext.Provider>
  );
};
